import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { NgClass } from '@angular/common';

/**
 * The wizard's step row, and its second navigation control.
 *
 * ⚠️ Deliberately NOT a "progress indicator", which is what it used to be called and what the
 * numbered bubbles made it look like. Most steps are reachable at any time and several are
 * optional, so the row states WHERE THE ADVISOR IS and where they have been -- not how far
 * through a fixed sequence they have got. See the template for what the glyphs mean.
 *
 * Which steps are reachable is the parent's decision, arriving as `navigableSteps` — the
 * indicator neither knows nor infers it. Reachability is not "already visited": the early
 * steps are optional, so an advisor can jump straight to the one they came for.
 *
 * `lockedSteps` is the same idea for the opposite case: steps the parent has CLOSED, which
 * render dimmed and still take a click so they can explain themselves. The indicator does not
 * know why one is closed and must not learn — it reports the click and the parent says why.
 *
 * ⚠️ A step can be un-navigable WITHOUT being locked, and the two look different on purpose. An
 * ordinary forward step blocked by an unfinished one earlier is simply not yet clickable, and its
 * reason already shows in the footer next to Next. A locked step has no such reason on screen,
 * which is why it gets the dimming and the dialog.
 *
 * Built from the shared mint/gray tokens.
 */
@Component({
  selector: 'app-rav2-step-indicator',
  standalone: true,
  // NgClass only, for the one `hover:` utility that has to be conditional -- a hover affordance
  // on a step that cannot be clicked would invite a click that does nothing. `[class.x]` cannot
  // carry a Tailwind variant's colon reliably, and there is no component stylesheet to put a
  // rule in.
  imports: [NgClass],
  templateUrl: './step-indicator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepIndicatorComponent {
  /** Step labels, in order. */
  readonly steps = input.required<string[]>();
  /** 1-based index of the active step. */
  readonly current = input.required<number>();
  /** The 1-based steps the advisor may jump to right now. */
  readonly navigableSteps = input<ReadonlySet<number>>(new Set<number>());
  /** The 1-based steps that are closed and should say so when clicked. */
  readonly lockedSteps = input<ReadonlySet<number>>(new Set<number>());
  /** Emitted with the 1-based step index when a navigable step is clicked. */
  readonly stepSelected = output<number>();
  /** Emitted with the 1-based step index when a LOCKED step is clicked, so the parent can explain. */
  readonly lockedStepSelected = output<number>();

  /** Whether a 1-based step index can be navigated to. */
  isNavigable(step: number): boolean {
    return step !== this.current() && this.navigableSteps().has(step);
  }

  /**
   * Whether a 1-based step is closed.
   *
   * The current step is never treated as locked even if the parent lists it: dimming the step the
   * advisor is looking at, and offering to explain why they cannot go there, would be nonsense.
   */
  isLocked(step: number): boolean {
    return step !== this.current() && this.lockedSteps().has(step);
  }

  /** Clickable either way — one navigates, the other explains. */
  isActionable(step: number): boolean {
    return this.isNavigable(step) || this.isLocked(step);
  }

  /**
   * Classes for a step's LABEL, chosen in one place rather than as four independent bindings.
   *
   * ⚠️ Precedence has to be decided HERE, not by CSS. Every one of these is a utility class, so
   * they all carry the same specificity and the winner is whichever appears later in the
   * generated stylesheet — an order nothing in this template controls. Two colour classes both
   * applied to one element is a coin toss, and it is exactly what happens when a locked colour is
   * added alongside the state colours without excluding them.
   *
   * Locked comes first because it OVERRIDES the state rather than shading it. A locked step that
   * has been visited was mint at 50% opacity, which reads as "mint, faded" rather than
   * "unavailable" — the colour still said "you have been here", which is true and not the point.
   */
  labelClasses(step: number): string {
    const tone = this.labelTone(step);
    // Folded in rather than left as separate bindings: `ngClass` can only appear once on an
    // element, and splitting these across `[class.x]` bindings is what invites the precedence
    // problem described above.
    return this.isActionable(step) ? `${tone} cursor-pointer hover:underline` : tone;
  }

  private labelTone(step: number): string {
    if (this.isLocked(step)) return 'text-gray-light';
    if (step === this.current()) return 'text-mint-darkest font-semibold';
    if (step < this.current()) return 'text-mint-darker';
    return 'text-gray-dark';
  }

  /**
   * Classes for a step's CIRCLE. Same reasoning as {@link labelClasses}.
   *
   * A locked step keeps its ring but loses its fill: a filled mint disc is the visited state, and
   * a greyed-out filled disc still reads as a state the advisor reached and can return to.
   */
  bubbleClasses(step: number): string {
    if (this.isLocked(step)) return 'border-2 border-gray-lighter text-gray-light';
    if (step === this.current()) return 'text-mint-darkest';
    if (step < this.current()) return 'border-2 bg-mint-darker border-mint-darker text-white';
    return 'border-2 border-gray-light';
  }

  select(step: number): void {
    if (this.isLocked(step)) this.lockedStepSelected.emit(step);
    else if (this.isNavigable(step)) this.stepSelected.emit(step);
  }
}
